import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Plus, Sparkles } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import Navbar from "../components/Navbar";
import { useAuth } from "../context/AuthContext";

export default function Dashboard() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/assessments");
        setItems(data.items || []);
      } catch (e) {
        setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      }
    })();
  }, []);

  const latest = items && items.length ? items[0] : null;

  return (
    <div className="App" data-testid="dashboard-page">
      <Navbar />
      <section className="border-b border-[var(--es-line)]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <p className="font-ui text-xs tracking-[0.28em] uppercase text-[var(--es-blue)]">
              Welcome back, {user?.name || user?.email}
            </p>
            <h1 className="font-display text-5xl sm:text-6xl text-[var(--es-ink)] tracking-tight mt-3 leading-[1.02]">
              Your <em className="text-[var(--es-blue)]">archive</em> of readings.
            </h1>
            <p className="font-body text-[var(--es-ink-soft)] mt-4 max-w-xl">
              Each assessment is a snapshot — a still frame of a self in motion. Retake periodically
              to watch your shape change.
            </p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link
              to="/quiz"
              className="bg-[var(--es-blue)] text-white px-8 py-4 hover:bg-[var(--es-blue-hover)] transition-colors uppercase tracking-wider text-sm font-semibold font-ui inline-flex items-center gap-2"
              data-testid="dashboard-new-assessment-button"
            >
              <Plus size={14} strokeWidth={1.8} /> New assessment
            </Link>
          </div>
        </div>
      </section>

      {error && (
        <p className="max-w-7xl mx-auto px-6 md:px-12 py-6 text-red-600 font-body">{error}</p>
      )}

      {latest && (
        <section className="border-b border-[var(--es-line)]">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16 grid grid-cols-1 md:grid-cols-12 gap-8 items-stretch">
            <div className="md:col-span-5 bg-[var(--es-blue)] text-white p-10">
              <p className="font-ui text-[11px] tracking-widest uppercase text-white/60">
                Latest reading
              </p>
              <p className="font-display text-7xl mt-2 leading-none" data-testid="dashboard-latest-overall">
                {latest.overall}
                <span className="text-white/50 text-3xl"> / 10</span>
              </p>
              <p className="font-display text-2xl mt-2">{latest.tier?.label}</p>
              <p className="font-body text-white/70 mt-3 text-sm">
                {new Date(latest.created_at).toLocaleString()}
              </p>
              <Link
                to={`/results/${latest.id}`}
                className="mt-8 inline-flex items-center gap-2 font-ui text-xs uppercase tracking-widest border-b border-white/40 pb-1 hover:border-white"
                data-testid="dashboard-view-latest-link"
              >
                View full reading <ArrowUpRight size={14} strokeWidth={1.6} />
              </Link>
            </div>
            <div className="md:col-span-7 grid grid-cols-2 sm:grid-cols-5 gap-px bg-[var(--es-line)] border border-[var(--es-line)]">
              {latest.domain_scores.map((d) => (
                <div key={d.key} className="bg-white p-5 flex flex-col justify-between min-h-[140px]">
                  <p className="font-ui text-[10px] tracking-widest uppercase text-[var(--es-ink-soft)]">
                    {d.label}
                  </p>
                  <p className="font-display text-4xl text-[var(--es-blue)]">{d.score.toFixed(1)}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section>
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16">
          <h2 className="font-display text-3xl text-[var(--es-ink)] mb-6">History</h2>
          {items === null ? (
            <p className="font-ui text-xs uppercase tracking-widest text-[var(--es-ink-soft)]">Loading…</p>
          ) : items.length === 0 ? (
            <div className="border border-dashed border-[var(--es-line)] p-12 text-center">
              <Sparkles size={24} strokeWidth={1.4} className="mx-auto text-[var(--es-blue)]" />
              <p className="font-display text-2xl text-[var(--es-ink)] mt-4">
                No readings yet — let's take your first.
              </p>
              <Link
                to="/quiz"
                className="mt-6 inline-block bg-[var(--es-blue)] text-white px-8 py-4 hover:bg-[var(--es-blue-hover)] transition-colors uppercase tracking-wider text-sm font-semibold font-ui"
                data-testid="dashboard-empty-cta"
              >
                Begin assessment
              </Link>
            </div>
          ) : (
            <ul className="border-t border-[var(--es-line)]">
              {items.map((it) => (
                <li
                  key={it.id}
                  className="grid grid-cols-12 gap-4 items-baseline border-b border-[var(--es-line)] py-4 px-2 hover:bg-[var(--es-bg-soft)] transition-colors"
                  data-testid={`dashboard-history-row-${it.id}`}
                >
                  <span className="col-span-3 sm:col-span-3 font-ui text-xs uppercase tracking-widest text-[var(--es-ink-soft)]">
                    {new Date(it.created_at).toLocaleDateString()}
                  </span>
                  <span className="col-span-2 font-display text-3xl text-[var(--es-blue)]">
                    {it.overall}
                  </span>
                  <span className="col-span-4 sm:col-span-5 font-display text-lg text-[var(--es-ink)]">
                    {it.tier?.label}
                  </span>
                  <span className="col-span-3 sm:col-span-2 text-right">
                    <Link
                      to={`/results/${it.id}`}
                      className="font-ui text-xs uppercase tracking-widest text-[var(--es-blue)] hover:underline inline-flex items-center gap-1"
                    >
                      View <ArrowUpRight size={12} strokeWidth={1.6} />
                    </Link>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}
